// Loads the investor list for admin pages/pickers. Fetches on mount; `reload`
// re-fetches (e.g. after creating an investor).
import { useCallback, useEffect, useState } from 'react';
import { captureException } from '@startus/lib';
import { investorsService } from '../services/investors.service';
import type { Investor } from '../types/investor';

interface UseInvestorsResult {
  investors: Investor[];
  loading: boolean;
  error: unknown;
  reload: () => void;
}

export function useInvestors(): UseInvestorsResult {
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  // Bumped by reload() to re-run the fetch effect.
  const [version, setVersion] = useState(0);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    // Guards against setting state after unmount (e.g. switching back to platform mode).
    let cancelled = false;

    investorsService
      .listInvestors()
      .then((data) => {
        if (!cancelled) setInvestors(data);
      })
      .catch((err: unknown) => {
        captureException(err);
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [version]);

  return { investors, loading, error, reload };
}
